/**
 * MediaThumb - renders an inline chat media attachment (photo or video).
 *
 * Media descriptors come in two flavours:
 *   - { kind, url }    direct URL (author template media)
 *   - { kind, blobId } a reference into the IndexedDB mediaStore (user uploads)
 *
 * For blob refs we resolve a fresh object URL on mount and revoke it on unmount
 * to avoid leaks. Tapping the thumb calls `onOpen` with the resolved URL so the
 * parent can show the fullscreen pinch-zoom viewer.
 */
import { useState, useEffect } from 'preact/hooks';
import { getBlobUrl } from '../lib/mediaStore.js';

export function MediaThumb({ media, onOpen }) {
  const [url, setUrl] = useState(media?.url || null);

  useEffect(() => {
    if (media?.url) {
      setUrl(media.url);
      return undefined;
    }
    if (!media?.blobId) return undefined;
    let revoked = false;
    let current = null;
    getBlobUrl(media.blobId).then((u) => {
      if (revoked) {
        if (u) URL.revokeObjectURL(u);
        return;
      }
      current = u;
      setUrl(u);
    });
    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [media?.url, media?.blobId]);

  if (!media) return null;

  const open = () => {
    if (url) onOpen?.({ kind: media.kind, url });
  };

  return (
    <button type="button" class="chatmedia" onClick={open} aria-label="Open media">
      {!url ? (
        <span class="chatmedia__placeholder" />
      ) : media.kind === 'video' ? (
        <video
          class="chatmedia__el"
          src={url}
          muted
          playsinline
          preload="metadata"
        />
      ) : (
        <img class="chatmedia__el" src={url} alt="" loading="lazy" />
      )}
      {media.kind === 'video' && <span class="chatmedia__play">▶</span>}
    </button>
  );
}
